import * as React from 'react';

import { QueryClientProvider } from '@tanstack/react-query';

import { AlertMessageDTO } from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import { queryClientFactory } from 'root/apps/PurchaseOrder/NuorderOrdering/context/QueryClient';
import {
  deleteStatePurchaseLine,
  updateStateItemVendorNum,
  updateStatePurchaseLine,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Reducers';
import {
  Configs,
  Item,
  ItemVendorNum,
  NuorderBrand,
  NuorderOrder,
  NuorderProduct,
  NuorderProductOrderable,
  Purchase,
  PurchaseLine,
  SortingPreference,
  Vendor,
  VendorShop,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';
import {
  getItemDictFromArray,
  getNuorderProductAvailabilityDictFromArray,
  getNuorderProductDictFromArray,
} from 'root/apps/PurchaseOrder/NuorderOrdering/utils/dataUtils';
import { logError } from 'root/datadog/datadogLogs';

export const LOG_SOURCE = 'NuorderOrdering';

export interface NuorderOrderingState {
  purchase: Purchase | null;
  purchaseLines: PurchaseLine[] | null;
  items: Record<Item['itemID'], Item> | null;
  nuorderProducts: Record<NuorderProduct['upc'], NuorderProduct | NuorderProductOrderable> | null;
  nuorderProductAvailability?: Record<
    NuorderProductOrderable['id'],
    NuorderProductOrderable
  > | null;
  nuorderOrder: NuorderOrder | null;
  nuorderBrand: NuorderBrand | null;
  vendor: Vendor | null;
  vendorShop: VendorShop | null;
  alertMessage: AlertMessageDTO | null;
  isFetchingOrder: boolean;
  isFetchingProducts: boolean;
  isFetchingAvailability: boolean;
  configs: Configs | null;
  sortingPreference: SortingPreference | null;
  lastAction: NuorderOrderingActionTypes | null;
}

export const blankNuorderOrderingState: NuorderOrderingState = {
  purchase: null,
  purchaseLines: null,
  items: null,
  nuorderProducts: null,
  nuorderProductAvailability: null,
  nuorderOrder: null,
  nuorderBrand: null,
  vendor: null,
  vendorShop: null,
  alertMessage: null,
  isFetchingOrder: false,
  isFetchingProducts: false,
  isFetchingAvailability: false,
  configs: null,
  sortingPreference: null,
  lastAction: null,
};

export const NuorderOrderingActionTypes = {
  SET_PURCHASE: 'SET_PURCHASE',
  UPDATE_PURCHASE: 'UPDATE_PURCHASE',
  SET_PURCHASE_LINES: 'SET_PURCHASE_LINES',
  UPDATE_PURCHASE_LINE: 'UPDATE_PURCHASE_LINE',
  DELETE_PURCHASE_LINE: 'DELETE_PURCHASE_LINE',
  ADD_ITEMS: 'ADD_ITEMS',
  ADD_NUORDER_PRODUCTS: 'ADD_NUORDER_PRODUCTS',
  ADD_NUORDER_PRODUCT_AVAILABILITY: 'ADD_NUORDER_PRODUCT_AVAILABILITY',
  UPDATE_ITEM_VENDOR_NUM: 'UPDATE_ITEM_VENDOR_NUM',
  DELETE_ITEM_VENDOR_NUM: 'DELETE_ITEM_VENDOR_NUM',
  SET_NUORDER_ORDER: 'SET_NUORDER_ORDER',
  SET_NUORDER_BRAND: 'SET_NUORDER_BRAND',
  SET_VENDOR: 'SET_VENDOR',
  SET_VENDOR_SHOP: 'SET_VENDOR_SHOP',
  SET_ALERT_MESSAGE: 'SET_ALERT_MESSAGE',
  SET_IS_FETCHING_ORDER: 'SET_IS_FETCHING_ORDER',
  SET_IS_FETCHING_PRODUCTS: 'SET_IS_FETCHING_PRODUCTS',
  SET_IS_FETCHING_AVAILABILITY: 'SET_IS_FETCHING_AVAILABILITY',
  SET_CONFIGS: 'SET_CONFIGS',
  SET_SORTING_PREFERENCE: 'SET_SORTING_PREFERENCE',
} as const;

export type NuorderOrderingActionTypes =
  (typeof NuorderOrderingActionTypes)[keyof typeof NuorderOrderingActionTypes];

type NuorderOrderingActionPayloads = {
  [NuorderOrderingActionTypes.SET_PURCHASE]: Purchase;
  [NuorderOrderingActionTypes.UPDATE_PURCHASE]: Partial<Purchase>;
  [NuorderOrderingActionTypes.SET_PURCHASE_LINES]: PurchaseLine[];
  [NuorderOrderingActionTypes.UPDATE_PURCHASE_LINE]: PurchaseLine;
  [NuorderOrderingActionTypes.DELETE_PURCHASE_LINE]: number;
  [NuorderOrderingActionTypes.ADD_ITEMS]: Item[];
  [NuorderOrderingActionTypes.ADD_NUORDER_PRODUCTS]: NuorderProduct[];
  [NuorderOrderingActionTypes.ADD_NUORDER_PRODUCT_AVAILABILITY]: NuorderProductOrderable[];
  [NuorderOrderingActionTypes.UPDATE_ITEM_VENDOR_NUM]: ItemVendorNum;
  [NuorderOrderingActionTypes.DELETE_ITEM_VENDOR_NUM]: { itemID: number; vendorID: number };
  [NuorderOrderingActionTypes.SET_NUORDER_ORDER]: NuorderOrder;
  [NuorderOrderingActionTypes.SET_NUORDER_BRAND]: NuorderBrand;
  [NuorderOrderingActionTypes.SET_VENDOR]: Vendor;
  [NuorderOrderingActionTypes.SET_VENDOR_SHOP]: VendorShop | null;
  [NuorderOrderingActionTypes.SET_ALERT_MESSAGE]: AlertMessageDTO | null;
  [NuorderOrderingActionTypes.SET_IS_FETCHING_ORDER]: boolean;
  [NuorderOrderingActionTypes.SET_IS_FETCHING_PRODUCTS]: boolean;
  [NuorderOrderingActionTypes.SET_IS_FETCHING_AVAILABILITY]: boolean;
  [NuorderOrderingActionTypes.SET_CONFIGS]: Configs;
  [NuorderOrderingActionTypes.SET_SORTING_PREFERENCE]: SortingPreference;
};

type NuorderOrderingReducerAction = {
  [K in keyof NuorderOrderingActionPayloads]: {
    type: K;
    payload: NuorderOrderingActionPayloads[K];
  };
}[keyof NuorderOrderingActionPayloads];

const NuorderOrderingReducer: React.Reducer<NuorderOrderingState, NuorderOrderingReducerAction> = (
  newState,
  action
): NuorderOrderingState => {
  const state: NuorderOrderingState = { ...newState, lastAction: action.type };

  switch (action.type) {
    case NuorderOrderingActionTypes.SET_PURCHASE:
      state.purchase = action.payload;
      return state;
    case NuorderOrderingActionTypes.UPDATE_PURCHASE:
      if (state.purchase === null) {
        logError(new Error('Cannot update purchase when it is null'), {
          source: LOG_SOURCE,
          file: 'Context.tsx',
        });
        return state;
      }
      // This is to avoid relations like Note from being lost when updating the purchase
      state.purchase = { ...state.purchase, ...action.payload };
      return state;
    case NuorderOrderingActionTypes.SET_PURCHASE_LINES:
      state.purchaseLines = action.payload;
      return state;
    case NuorderOrderingActionTypes.UPDATE_PURCHASE_LINE:
      state.purchaseLines = updateStatePurchaseLine(state.purchaseLines, action.payload);
      return state;
    case NuorderOrderingActionTypes.DELETE_PURCHASE_LINE:
      state.purchaseLines = deleteStatePurchaseLine(state.purchaseLines, action.payload);
      return state;
    case NuorderOrderingActionTypes.ADD_ITEMS:
      if (state.items === null) state.items = {};
      if (action.payload.length === 0) return state;
      state.items = { ...state.items, ...getItemDictFromArray(action.payload) };
      return state;
    case NuorderOrderingActionTypes.ADD_NUORDER_PRODUCTS:
      if (state.nuorderProducts === null) state.nuorderProducts = {};
      if (action.payload.length === 0) return state;
      state.nuorderProducts = {
        ...state.nuorderProducts,
        ...getNuorderProductDictFromArray(action.payload),
      };
      return state;
    case NuorderOrderingActionTypes.ADD_NUORDER_PRODUCT_AVAILABILITY:
      if (state.nuorderProductAvailability === null) state.nuorderProductAvailability = {};
      if (action.payload.length === 0) return state;
      state.nuorderProductAvailability = {
        ...state.nuorderProductAvailability,
        ...getNuorderProductAvailabilityDictFromArray(action.payload),
      };
      if (state.nuorderProducts === null) state.nuorderProducts = {};
      state.nuorderProducts = {
        ...state.nuorderProducts,
        ...getNuorderProductDictFromArray(action.payload),
      };
      return state;
    case NuorderOrderingActionTypes.UPDATE_ITEM_VENDOR_NUM:
      const itemVendorNum = action.payload;
      state.items = updateStateItemVendorNum(
        state.items,
        itemVendorNum.itemID,
        itemVendorNum.vendorID,
        itemVendorNum
      );
      return state;
    case NuorderOrderingActionTypes.DELETE_ITEM_VENDOR_NUM:
      const { itemID, vendorID } = action.payload;
      state.items = updateStateItemVendorNum(state.items, itemID, vendorID, null);
      return state;
    case NuorderOrderingActionTypes.SET_NUORDER_ORDER:
      state.nuorderOrder = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_NUORDER_BRAND:
      state.nuorderBrand = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_VENDOR:
      state.vendor = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_VENDOR_SHOP:
      state.vendorShop = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_ALERT_MESSAGE:
      state.alertMessage = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_IS_FETCHING_ORDER:
      state.isFetchingOrder = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_IS_FETCHING_PRODUCTS:
      state.isFetchingProducts = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_IS_FETCHING_AVAILABILITY:
      state.isFetchingAvailability = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_CONFIGS:
      state.configs = action.payload;
      return state;
    case NuorderOrderingActionTypes.SET_SORTING_PREFERENCE:
      state.sortingPreference = action.payload;
      return state;
    default:
      return state;
  }
};

export interface NuorderOrderingContextProps {
  purchaseID: number;
  state: NuorderOrderingState;
  dispatch: React.Dispatch<NuorderOrderingReducerAction>;
}

const NuorderOrderingContext: React.Context<NuorderOrderingContextProps> = React.createContext(
  {} as NuorderOrderingContextProps
);

interface NuorderOrderingProviderProps {
  purchaseID: number;
  children: React.ReactNode;
  mockState?: NuorderOrderingState; // Optional for testing purposes
}

export const NuorderOrderingProvider: React.FC<NuorderOrderingProviderProps> = ({
  purchaseID,
  children,
  mockState = {},
}) => {
  const [state, dispatch] = React.useReducer(NuorderOrderingReducer, {
    ...blankNuorderOrderingState,
    ...mockState,
  });

  const queryClient = React.useMemo(() => {
    return queryClientFactory();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <NuorderOrderingContext.Provider value={{ purchaseID, state, dispatch }}>
        {children}
      </NuorderOrderingContext.Provider>
    </QueryClientProvider>
  );
};

export const useNuorderOrdering = () => {
  const context = React.useContext(NuorderOrderingContext);
  if (context.state === undefined) {
    throw new Error('useNuorderOrdering must be used within a NuorderOrderingProvider');
  }
  return context;
};
